/**
 * Login entry screen for the API-only OAuth authorization-code + PKCE flow.
 * The component stays mounted until authentication and initialization finish.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import toast from "react-hot-toast";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { exchangeAuthorizationCode, login, setNavigate } from "@service/user-handler";
import { getMetadata } from "@service/metadata-handler";
import { connectWS } from "@service/websocket-handler";
import {
  getRememberedCredentials,
  removeRememberedCredentials,
  setRememberedCredentials,
} from "@service/local-storage-handler";
import { usePostLoginSync } from "../../hooks/usePostLoginSync";
import { markSubscriptionReminderForLogin } from "../../hooks/useSubscriptionExpiryReminder";
import TitleBar from "@components/title-bar/TitleBar";

import "./Login.scss";

/** Extract a user-friendly message from an Axios error. */
const friendlyError = (error: any, fallback: string): string => {
  const serverMsg = error?.response?.data?.error || error?.response?.data?.message;
  if (serverMsg) return serverMsg;

  const status = error?.response?.status;
  if (status === 400) return "Invalid username or password";
  if (status === 401) return "Invalid credentials. Please try again";
  if (status === 403) return "Your account has been locked. Contact support";
  if (status === 404) return "Service unavailable. Please try later";
  if (status === 429) return "Too many attempts. Please wait and try again";
  if (status >= 500) return "Server error. Please try again later";

  if (typeof error?.message === "string" && error.message.trim().length > 0) {
    return error.message;
  }

  return fallback;
};

const Login = () => {
  const navigate = useNavigate();
  const runPostLoginSync = usePostLoginSync();
  const [isLoading, setIsLoading] = useState(false);
  const [rememberedCredentials] = useState(() => getRememberedCredentials());
  const [username, setUsername] = useState(rememberedCredentials.username);
  const [password, setPassword] = useState(rememberedCredentials.password);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const processingRef = useRef(false);

  useEffect(() => {
    setNavigate(navigate);
  }, [navigate]);

  const completeLogin = useCallback(
    async (code: string, state: string) => {
      if (processingRef.current) return;
      processingRef.current = true;
      await exchangeAuthorizationCode(code, state);
      markSubscriptionReminderForLogin();

      const initialization = await Promise.allSettled([
        getMetadata(),
        runPostLoginSync(),
      ]);
      initialization.forEach((result) => {
        if (result.status === "rejected") {
          console.warn("Post-login initialization failed:", result.reason);
        }
      });

      void connectWS().catch((error) => {
        console.warn("WebSocket connection failed after login:", error);
      });

      navigate("/Chart", { replace: true });
    },
    [navigate, runPostLoginSync]
  );

  const beginLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!username.trim() || !password) {
      toast.error("Enter your username and password");
      return;
    }
    setIsLoading(true);
    if (rememberMe) setRememberedCredentials({ username: username.trim(), password });
    else removeRememberedCredentials();
    try {
      const authorization = await login(username.trim(), password);
      await completeLogin(authorization.code, authorization.state);
    } catch (error) {
      processingRef.current = false;
      setIsLoading(false);
      toast.error(friendlyError(error, "Unable to sign in"));
    }
  };

  return (
    <div className="login-page">
      <TitleBar minimal />

      {/* Animated background */}
      <div className="bg-mesh">
        <div className="orb orb-1" />
        <div className="orb orb-2" />
        <div className="orb orb-3" />
      </div>

      <div className="login-center">
        {/* Card */}
        <div className="login-card">
          <h1>Welcome</h1>
          <p className="subtitle">Sign in to MarketView</p>

          <form className="login-form" onSubmit={beginLogin}>
            <div className="field">
              <label htmlFor="username">Username</label>
              <input
                id="username"
                name="username"
                type="text"
                value={username}
                onChange={(event) => setUsername(event.target.value.toUpperCase())}
                autoComplete="username"
                autoCapitalize="characters"
                placeholder="Enter your username"
                disabled={isLoading}
                autoFocus
                required
              />
            </div>

            <div className="field">
              <label htmlFor="password">Password</label>
              <div className="pwd-wrap">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  disabled={isLoading}
                  required
                />
                <button
                  type="button"
                  className="pwd-toggle"
                  onClick={() => setShowPassword((visible) => !visible)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  disabled={isLoading}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </div>

            <div className="options-row">
              <label className="remember">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(event) => setRememberMe(event.target.checked)}
                  disabled={isLoading}
                />
                <span className="check-box" />
                Remember me
              </label>
            </div>

            <button type="submit" className="btn-primary" disabled={isLoading}>
              {isLoading
                ? <><span className="spinner" /> Signing in…</>
                : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
